import { FC, useEffect, useState } from 'react';
import { DraggableTabList, Tab } from 'components/draggable-tab-list/DraggableTabList';
import CustomButton from 'components/custom-button/CustomButton';
import { RedirectIcon } from 'assets/icons';
import { openInNewTab } from 'utils/commonUtils';
import CustomPopover from 'components/custom-popover/CustomPopover';
import { TeamView } from 'containers/dispatch/dispatch.model';
import routesPath from 'routes/RoutesPath';
import { setJobsCurrentActiveView } from 'containers/jobs/jobsSlice';
import { useAppDispatch, useAppSelector } from 'hooks/typedHooks';
import { selectCurrentTeam, selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import { selectJobsCurrentActiveView } from 'containers/jobs/jobsSlice';
import CreateViewModal from 'containers/dispatch/components/CreateViewModal';
import ViewsPopover from 'containers/dispatch/components/ViewsPopover';
import {
  useCreateJobsViewMutation,
  useMarkJobsViewAsLastViewedMutation,
  useUpdateJobsViewPriorityMutation
} from '../api';
import { useViewFilters } from '../hooks/useViewFilters';
import { formatJobViewFiltersForAPI } from '../utils';

interface JobsViewsBarProps {
  views: TeamView[];
  isTeamViewsLoading: boolean;
}

const JobsViewsBar: FC<JobsViewsBarProps> = ({ views, isTeamViewsLoading }) => {
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const currentActiveView = useAppSelector(selectJobsCurrentActiveView);
  const currentJobSettingsPermissions = useAppSelector(selectCurrentTeamJobPermissions);
  const { editJobSettings } = currentJobSettingsPermissions ?? {};

  // Use the new custom hook for view filter management
  const { saveCurrentViewFilters, loadViewFilters, currentViewFilters, updateCurrentViewFilters } = useViewFilters();

  const [activeTab, setActiveTab] = useState(null);
  const [isViewsPopoverOpen, setIsViewsPopoverOpen] = useState(false);
  const [isCreateViewModalOpen, setIsCreateViewModalOpen] = useState(false);
  const [viewTabs, setViewTabs] = useState<Tab[]>();

  const [createJobsView, { isLoading: isCreatingView }] = useCreateJobsViewMutation();
  const [updateJobsViewPriority, { isLoading: isUpdatingViewPriority }] = useUpdateJobsViewPriorityMutation();
  const [markJobsViewAsLastViewed] = useMarkJobsViewAsLastViewedMutation();

  useEffect(() => {
    setViewTabs(
      views
        .filter(view => view.isEnabled)
        .map(view => ({
          id: view.id.toString(),
          label: view.name,
          count: 0,
          showClose: true
        }))
    );
  }, [views]);

  useEffect(() => {
    if (currentActiveView) {
      setActiveTab(currentActiveView.id.toString());
      // Load filters for the current active view using the custom hook
      loadViewFilters(currentActiveView);
    }
  }, [currentActiveView?.id, loadViewFilters]);

  // Clear all view filters when team changes (optional - depends on business requirements)
  useEffect(() => {
    if (currentTeam?.id) {
      // Optionally clear all saved view filters when team changes
      // dispatch(clearAllViewFilters());
    }
  }, [currentTeam?.id, dispatch]);

  const getViewTabs = (viewsParams: TeamView[]) => {
    return viewsParams
      .filter(view => view.isEnabled)
      .map(view => ({
        id: view.id.toString(),
        label: view.name,
        count: 0,
        showClose: true
      }));
  };

  const handleTabReorder = async (newOrder: Tab[], oldOrder: Tab[]) => {
    const viewIds = newOrder.map(tab => parseInt(tab.id));
    const oldViewIds = oldOrder.map(tab => parseInt(tab.id));

    const arraysAreEqual = (arr1: number[], arr2: number[]) => {
      if (arr1.length !== arr2.length) return false;
      return arr1.every((value, index) => value === arr2[index]);
    };

    if (!arraysAreEqual(viewIds, oldViewIds)) {
      await updateJobsViewPriority({ teamId: currentTeam?.id, body: { viewIds } });
    }

    setViewTabs(newOrder);
  };

  const handleManageViews = () => {
    openInNewTab(routesPath.MAP_VIEWS_SETTINGS);
  };

  const onTabChange = (tabId: string) => {
    // Save current filters before switching
    saveCurrentViewFilters();

    setActiveTab(tabId);
    const selectedView = views.find(view => view.id.toString() === tabId);

    if (selectedView) {
      dispatch(setJobsCurrentActiveView(selectedView));
      // The useEffect above will handle loading the filters for the new view
      markJobsViewAsLastViewed({ teamId: currentTeam?.id, viewId: selectedView.id });
    }
  };

  const handleCreateView = async (viewName: string) => {
    try {
      const formattedFilters = formatJobViewFiltersForAPI(currentViewFilters);

      const newView = await createJobsView({
        teamId: currentTeam?.id,
        body: {
          name: viewName,
          type: 'job',
          filters: formattedFilters
        }
      }).unwrap();

      if (newView?.data) {
        setActiveTab(newView.data.id.toString());
        dispatch(setJobsCurrentActiveView(newView.data));

        if (currentTeam?.id) {
          markJobsViewAsLastViewed({ teamId: currentTeam.id, viewId: newView.data.id });
        }
      }
    } catch (error) {
      //error
    } finally {
      setIsCreateViewModalOpen(false);
    }
  };

  const handleViewSelection = (viewIds: number[]) => {
    const newTabs = getViewTabs(views.filter(view => viewIds.includes(view.id)));
    setViewTabs(newTabs);
    updateJobsViewPriority({ teamId: currentTeam?.id, body: { viewIds } });
    handleCloseViewsPopover();
  };

  const handleRemoveTab = async (tabId: string) => {
    const filteredViews = views.filter(view => view.isEnabled && view.id.toString() !== tabId);
    const viewIds = filteredViews.map(view => view.id);
    const newTabs = getViewTabs(filteredViews);
    await updateJobsViewPriority({ teamId: currentTeam?.id, body: { viewIds } });
    setViewTabs(newTabs);
    onTabChange(newTabs[0].id);
  };

  const handleOpenCreateViewModal = () => {
    setIsCreateViewModalOpen(true);
  };

  const handleCloseCreateViewModal = () => {
    setIsCreateViewModalOpen(false);
  };

  const handleCloseViewsPopover = () => {
    setIsViewsPopoverOpen(false);
  };

  const addViewButton = () => (
    <CustomButton
      id="add-view-button"
      type={null}
      className={'h-8 px-3 font-semibold text-secondary hover:shadow-none'}>
      + Add view
    </CustomButton>
  );

  return (
    <div className="flex items-center">
      <div className="h-full max-w-[70%]">
        <DraggableTabList
          tabs={viewTabs || []}
          activeTab={activeTab}
          onTabChange={onTabChange}
          onReorder={handleTabReorder}
          onRemoveTab={handleRemoveTab}
          className="[&>*]:border-y [&>*]:border-borderGray"
        />
      </div>
      <div className="flex h-full shrink-0 grow items-center justify-between border-b border-borderGray">
        <CustomPopover
          margin="mt-2"
          padding="p-0"
          anchorComponent={addViewButton}
          show={isViewsPopoverOpen}
          onTogglePopover={() => setIsViewsPopoverOpen(!isViewsPopoverOpen)}
          onClosePopover={handleCloseViewsPopover}
          offset="-left-4"
          width="w-[280px]">
          <ViewsPopover
            onClose={handleCloseViewsPopover}
            onOpenCreateViewModal={handleOpenCreateViewModal}
            views={views}
            handleViewSelection={handleViewSelection}
            hasCreateViewPermission={editJobSettings}
            isTeamViewsLoading={isTeamViewsLoading || isUpdatingViewPriority}
          />
        </CustomPopover>

        <div className="ml-auto">
          <CustomButton
            id="manage-views-button"
            type={null}
            className="h-8 px-3 font-semibold text-secondary"
            onClick={handleManageViews}
            EndIcon={RedirectIcon}
            endIconStyle="w-4 h-4">
            Manage views
          </CustomButton>
        </div>
      </div>
      {isCreateViewModalOpen && (
        <CreateViewModal
          onClose={handleCloseCreateViewModal}
          isOpen={isCreateViewModalOpen}
          onSave={handleCreateView}
          isSaving={isCreatingView}
        />
      )}
    </div>
  );
};

export default JobsViewsBar;
